import { useCallback, useMemo, useRef } from "react";
import type {
  AgentAction,
  AgentCommandMessage,
  AgentCommandResult,
  IframeToParentMessage,
} from "../../../shared/liveBrowserRuntime/protocol";
import type { LiveFeature } from "./feature";

const READY_TIMEOUT_MS = 10_000;

export type { AgentAction, AgentCommandResult };

// The tail of `AgentCommandMessage` minus the wire-envelope fields
// (`type` and `id`) that `send` fills in itself. A discriminated union so
// TypeScript keeps each `cmd`'s extra fields tied to its kind.
type AgentCommandPayload =
  | { cmd: "inspect"; selector?: string }
  | { cmd: "console" }
  | { cmd: "screenshot" }
  | { cmd: "interact"; actions: AgentAction[]; selector?: string };

export type AgentControl = {
  feature: LiveFeature;
  // Resolves once the iframe has attached its port and reported ready.
  whenReady: () => Promise<void>;
  inspect: (selector?: string) => Promise<AgentCommandResult>;
  getConsole: () => Promise<AgentCommandResult>;
  interact: (
    actions: AgentAction[],
    selector?: string
  ) => Promise<AgentCommandResult>;
  screenshot: () => Promise<AgentCommandResult>;
};

// The agent's counterpart to useInspectMode: opens a request/response
// channel to the live-browser runtime's agent-control module and exposes
// promise-returning commands over the MessagePort. The runtime bundle is
// injected by the public service; this hook just talks to it.
export default function useAgentControl(): AgentControl {
  const portRef = useRef<MessagePort | null>(null);
  const readyRef = useRef(false);
  const readyWaitersRef = useRef<(() => void)[]>([]);
  const pendingRef = useRef(
    new Map<string, (result: AgentCommandResult) => void>()
  );
  const idRef = useRef(0);

  // Rejects rather than hanging if the iframe never reports ready — e.g.
  // the page loads outside preview mode so no runtime is injected. The
  // pre-flight in driveLiveStep should catch that first; this is the
  // backstop.
  const whenReady = useCallback(
    () =>
      new Promise<void>((resolve, reject) => {
        if (readyRef.current) {
          resolve();
          return;
        }
        const timer = setTimeout(() => {
          readyWaitersRef.current = readyWaitersRef.current.filter(
            (w) => w !== waiter
          );
          reject(new Error("The page preview did not finish loading."));
        }, READY_TIMEOUT_MS);
        const waiter = () => {
          clearTimeout(timer);
          resolve();
        };
        readyWaitersRef.current.push(waiter);
      }),
    []
  );

  const send = useCallback(
    (command: AgentCommandPayload) =>
      new Promise<AgentCommandResult>((resolve) => {
        const port = portRef.current;
        if (!port) {
          resolve({ error: "Preview is not ready" });
          return;
        }
        const id = `cmd-${idRef.current++}`;
        pendingRef.current.set(id, resolve);
        try {
          const message: AgentCommandMessage = {
            type: "3b-agent-cmd",
            id,
            ...command,
          };
          port.postMessage(message);
        } catch {
          pendingRef.current.delete(id);
          resolve({ error: "Failed to send command" });
        }
      }),
    []
  );

  const feature = useMemo<LiveFeature>(
    () => ({
      attach: (api) => {
        const port = api.port;
        portRef.current = port;
        if (!port) return;
        const onMessage = (event: MessageEvent) => {
          const data = event.data as IframeToParentMessage | undefined;
          if (!data) return;
          if (data.type === "3b-agent-ready") {
            readyRef.current = true;
            for (const w of readyWaitersRef.current) w();
            readyWaitersRef.current = [];
            return;
          }
          if (data.type === "3b-agent-result") {
            const resolve = pendingRef.current.get(data.id);
            if (resolve) {
              pendingRef.current.delete(data.id);
              const { type: _type, id: _id, ...result } = data;
              resolve(result);
            }
          }
        };
        port.addEventListener("message", onMessage);
        port.start();
        return () => {
          port.removeEventListener("message", onMessage);
          portRef.current = null;
          readyRef.current = false;
        };
      },
    }),
    []
  );

  const inspect = useCallback(
    (selector?: string) => send({ cmd: "inspect", selector }),
    [send]
  );

  const getConsole = useCallback(() => send({ cmd: "console" }), [send]);

  const interact = useCallback(
    (actions: AgentAction[], selector?: string) =>
      send({ cmd: "interact", actions, selector }),
    [send]
  );

  const screenshot = useCallback(() => send({ cmd: "screenshot" }), [send]);

  return { feature, whenReady, inspect, getConsole, interact, screenshot };
}
